import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { Clock, ArrowRight, BookOpen } from 'lucide-react';

const BLOG_POSTS = [
  {
    slug: 'how-to-complete-480-cda-hours',
    title: 'How to Complete the 480 Hours Required for Your CDA',
    excerpt: 'The 480-hour experience requirement is one of the biggest challenges CDA candidates face. Learn where to find placements, how to track your hours, and what counts toward your requirement.',
    category: 'Experience Hours',
    readTime: '8 min read',
    date: 'December 2025',
    link: '/cda-480-hours-support',
  },
  {
    slug: 'what-to-do-after-120-hours',
    title: 'What to Do After Completing Your 120 CDA Training Hours',
    excerpt: 'You\'ve finished your 120 hours of professional education — congratulations! Here\'s exactly what comes next in your CDA journey.',
    category: 'Getting Started',
    readTime: '6 min read',
    date: 'December 2025',
    link: '/cda-portfolio-help',
  },
  {
    slug: 'cda-portfolio-checklist-texas',
    title: 'CDA Portfolio Checklist for Texas Candidates',
    excerpt: 'A complete checklist of everything you need in your CDA Professional Portfolio, organized by the six competency areas required by the CDA Council.',
    category: 'Portfolio',
    readTime: '10 min read',
    date: 'December 2025',
    link: '/cda-portfolio-help',
  },
  {
    slug: 'care-comply-connects-candidates-centers',
    title: 'How Care Comply Connects CDA Candidates and Learning Centers',
    excerpt: 'Care Comply bridges the gap between aspiring educators who need experience hours and childcare centers looking for motivated candidates.',
    category: 'Partnerships',
    readTime: '5 min read',
    date: 'December 2025',
    link: '/learning-center-partnerships',
  },
  {
    slug: 'true-cost-cda-credential',
    title: 'The True Cost of Earning a CDA Credential',
    excerpt: 'From training fees to the $525 assessment cost — here\'s a realistic breakdown of what you\'ll spend and how to find funding assistance.',
    category: 'Funding',
    readTime: '7 min read',
    date: 'December 2025',
    link: '/cda',
  },
  {
    slug: 'cda-career-pathways-san-antonio',
    title: 'CDA Career Pathways in San Antonio',
    excerpt: 'Your CDA credential opens doors to many career paths in early childhood education. Explore the opportunities available in San Antonio and South Texas.',
    category: 'Career Growth',
    readTime: '6 min read',
    date: 'December 2025',
    link: '/cda-job-readiness',
  },
];

const CdaResources = () => {
  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>CDA Resources & Articles | Care Comply</title>
        <meta name="description" content="Free CDA resources, guides, and articles to help you earn your Child Development Associate credential. Tips on 480 hours, portfolio building, job readiness, and more." />
        <link rel="canonical" href="https://carecomplycda.com/cda-resources" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Link to="/cda" className="text-sm text-[#0d7d6f] hover:underline">
            Start Your CDA Journey →
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0d7d6f]/10 text-[#0d7d6f] text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" /> Free Resources
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CDA Resources & Articles
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Free guides and articles to help you navigate your CDA journey — from understanding requirements to landing your first job.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {BLOG_POSTS.map((post) => (
            <Link
              key={post.slug}
              to={post.link}
              className="bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-lg transition-shadow group"
              data-testid={`blog-${post.slug}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="px-2 py-1 bg-[#0d7d6f]/10 text-[#0d7d6f] text-xs font-medium rounded-full">
                  {post.category}
                </span>
                <span className="flex items-center gap-1 text-xs text-slate-400">
                  <Clock className="w-3 h-3" /> {post.readTime}
                </span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-[#0d7d6f] transition-colors" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {post.title}
              </h2>
              <p className="text-slate-600 text-sm mb-4">
                {post.excerpt}
              </p>
              <div className="flex items-center text-[#0d7d6f] text-sm font-medium">
                Read more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#0d7d6f] to-[#0a6358] rounded-2xl p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to Start Your CDA Journey?</h2>
          <p className="mb-6 opacity-90">Care Comply guides you through every step — from training hours to your credential.</p>
          <Link
            to="/cda"
            className="inline-flex items-center gap-2 bg-white text-[#0d7d6f] px-6 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors"
          >
            Get Started Today <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="mt-12 text-center text-sm text-slate-500">
          <p>Questions? Chat with Jazmin, your AI CDA coach, inside Care Comply.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline ml-2">480-Hour Support</Link> • 
            <Link to="/about-dr-gabriela-ibarra-noriega" className="text-[#0d7d6f] hover:underline ml-2">About the Founder</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CdaResources;
